import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAuth, useFeatureFlags } from '@node-space/hooks'
import { Box, Tab, Tabs } from '@node-space/ui-components'
import { WalletContextProvider } from 'contexts/walletContext'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useWalletQuery } from 'hooks/queries'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ReceiveCrypto } from 'pages/Channels/ReceiveCrypto'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { CustomerWalletDetails } from 'types/customers'
import { WalletType } from 'types/types'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'
import { ExtendedDetails } from './components/ExtendedDetails/ExtendedDetails'
import { RecentActivity } from './components/RecentActivity/RecentActivity'
import { WalletDetailsHeader } from './components/WalletDetailsHeader/WalletDetailsHeader'
import { WalletNotFound } from './components/WalletNotFound/WalletNotFound'

type TabQuery = 'recent-activity' | 'extended-details' | string

interface WalletDetailsProps {
  customerWalletDetails?: CustomerWalletDetails
}

export const WalletDetails = ({ customerWalletDetails }: WalletDetailsProps) => {
  const { t } = useTranslation()
  const {
    enableFiatCurrencyFilter,
    enableWalletDetailsTab,
    enableUsLaunchRestrictions,
    enableFiatPayinCapabilityV2,
    enableFiatPayoutCapabilityV2,
  } = useFeatureFlags()
  const queryClient = useQueryClient()
  const {
    currentAccount,
    isLoading: isLoadingCurrentAccount,
    accountCapabilities,
  } = useAccountsContext()
  const { isEpcWalletPayinsCapabilityEnabled } = accountCapabilities || {}
  const { isAuthed } = useAuth()
  const { id }: { id?: string } = useParams()

  const customerModel = useAtomValue(customerModelAtom)
  const isEmbeddedOrRelianceCustomerModel =
    customerModel === CustomerModels.EMBEDDED || customerModel === CustomerModels.RELIANCE
  const { tab } = queryString.parse(window.location.search) as { tab?: TabQuery }
  const [activeTab, setActiveTab] = useState(tab ?? 'recent-activity')

  const walletId = useMemo(
    () => customerWalletDetails?.legacyWalletId ?? id,
    [customerWalletDetails?.legacyWalletId, id]
  )

  const { data: walletData, isPending, isError } = useWalletQuery(Number(walletId), !!walletId)
  const permissions = useMappedRoles()?.wallets
  const isSelectedWalletActive = useIsActiveWallet(walletData)

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (!!walletData?.lsid && currentAccount?.reference && isAuthed && !isLoadingCurrentAccount) {
      return getWalletActionCapabilityRequestFilters(
        [walletData?.lsid],
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2
      )
    }
  }, [walletData, currentAccount?.reference, isAuthed, isLoadingCurrentAccount])

  const { data: bulkCapabilities, isFetching: isFetchingWalletCapabilities } =
    useBulkCapabiltiesQuery(
      fiatWalletCapabilitiesRequest,
      !!fiatWalletCapabilitiesRequest,
      'walletDetailsCapabilitiesCheck'
    )

  const isLoading = isPending || isFetchingWalletCapabilities

  const wallet: WalletType = useMemo(() => {
    const walletProperties = {
      currency: walletData?.currency?.code,
      supportsWithdrawals: walletData?.supportsWithdrawals,
      supportsThirdParty: walletData?.supportsThirdParty,
      supportsDeposits: walletData?.supportsDeposits,
      lsid: walletData?.lsid,
      isLegacyWallet: !walletData?.supportedTransferDestinations?.length,
    }

    const { supportsPay, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
      getFiatWalletActionSupport({
        walletProperties,
        bulkCapabilities,
        enableFiatCurrencyFilter,
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2,
      })

    return {
      ...walletData,
      supportsDeposits: supportsDeposits,
      supportsWithdrawals: supportsWithdrawals,
      supportsThirdParty: supportsPay,
      supportsInternalTransfers: supportsInternalTransfers,
    }
  }, [bulkCapabilities, walletData])

  const canEdit = permissions?.canEdit
  const canTrade = permissions?.canTrade
  const isFiatWallet = wallet?.currency?.fiat

  useEffect(() => {
    if (wallet?.supportsInternalBvnkNetworkTransfers && !!currentAccount?.reference) {
      queryClient.invalidateQueries({
        queryKey: [
          'bvnkNetworkConnections',
          {
            accountReference: currentAccount?.reference,
          },
        ],
      })
    }
  }, [wallet?.supportsInternalBvnkNetworkTransfers, currentAccount?.reference])

  const showWalletDetailsHeader = (canEdit || canTrade) && !customerWalletDetails
  const isExtendedWalletDetailsAvailable =
    (isFiatWallet && wallet?.supportsDeposits) || (!isFiatWallet && !wallet?.supportsThirdParty)

  const isWalletDetailsTabVisible = useMemo(() => {
    const isVisible =
      isExtendedWalletDetailsAvailable &&
      isSelectedWalletActive &&
      (isFiatWallet || (enableWalletDetailsTab && !isFiatWallet))

    if (customerWalletDetails && isEmbeddedOrRelianceCustomerModel) {
      return isEmbeddedOrRelianceCustomerModel && isEpcWalletPayinsCapabilityEnabled
    }

    return isVisible
  }, [
    isExtendedWalletDetailsAvailable,
    isSelectedWalletActive,
    isFiatWallet,
    enableWalletDetailsTab,
    isEmbeddedOrRelianceCustomerModel,
    customerWalletDetails,
    isEpcWalletPayinsCapabilityEnabled,
  ])

  const isReceiveCryptoTabVisible = useMemo(() => {
    const isVisible =
      isExtendedWalletDetailsAvailable && isSelectedWalletActive && !enableUsLaunchRestrictions

    if (customerWalletDetails && isEmbeddedOrRelianceCustomerModel) {
      return isVisible && isEpcWalletPayinsCapabilityEnabled
    }

    return isVisible
  }, [
    customerWalletDetails,
    isExtendedWalletDetailsAvailable,
    isSelectedWalletActive,
    enableUsLaunchRestrictions,
    isEmbeddedOrRelianceCustomerModel,
    isEpcWalletPayinsCapabilityEnabled,
  ])

  const tabs: Tab[] = [
    {
      id: 'recent-activity',
      header: t('wallets.transactionHistory'),
      content: <RecentActivity isLoading={isLoading} wallet={wallet} />,
      testId: 'wallet-details-transaction-history-tab',
    },
  ]

  if (isWalletDetailsTabVisible) {
    const walletDetailsTab = {
      id: 'extended-details',
      header: t('walletDetails'),
      content: <ExtendedDetails />,
      testId: 'wallet-details-extended-details-tab',
    }
    tabs.push(walletDetailsTab)
  }

  if (isReceiveCryptoTabVisible) {
    const receiveCryptoTab = {
      id: 'receive-crypto',
      header: t('receiveCrypto'),
      content: <ReceiveCrypto />,
      testId: 'wallet-details-receive-crypto-tab',
    }
    tabs.push(receiveCryptoTab)
  }

  return (
    <WalletContextProvider wallet={wallet}>
      <Box>
        {isError && <WalletNotFound />}

        {showWalletDetailsHeader && !isError && <WalletDetailsHeader isLoading={isLoading} />}

        {!isError && !isExtendedWalletDetailsAvailable && (
          <RecentActivity
            isLoading={isLoading}
            wallet={wallet}
            customerWallet={customerWalletDetails}
          />
        )}

        {!isError && isExtendedWalletDetailsAvailable && (
          <Tabs tabs={tabs} defaultTabId={activeTab} />
        )}
      </Box>
    </WalletContextProvider>
  )
}
